import React, { useCallback } from 'react';
import type { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { ImmutableRootState, Langcodes } from '@uncinc/react-drupal-core/types';
import { SearchFacetRefinementSelect, SearchFacetMenuRadios, SearchFacets } from '@uncinc/react-searchkit';

import { APP_OPTIONS, DEFAULT_LANGUAGE } from '../../../../config/constants';
import sortSearchItemsByWeight from '../../../../helpers/sortSearchItemsByWeight';
import type { SearchAllContentFacet } from '../../../SearchAllContent/types';
import { facetAttributes } from '../config';

export interface MetaSearchFacetsProps {
  facets?: SearchAllContentFacet[];
}

const MetaSearchFacets: React.FC<MetaSearchFacetsProps> = (props) => {
  const { facets } = props;
  const { t } = useTranslation('translation', { keyPrefix: 'searchkit.meta_search.facets' });

  const langcode = useSelector<ImmutableRootState<Langcodes>, Langcodes>((state) => state.getIn(['ui', 'language'], DEFAULT_LANGUAGE));

  // We need to wrap this function in a useCallback to prevent an infinite loop.
  const transformItems = useCallback((items: MenuItem[]) => (
    sortSearchItemsByWeight(items, APP_OPTIONS.parameters.profileFunctionGroupWeights, langcode)
  ), [langcode]);

  return (
    <SearchFacets className="MetaSearch__Facets">
      {facetAttributes.map((facetAttribute) => {
        const attribute = typeof facetAttribute === 'object' ? facetAttribute.attribute : facetAttribute;

        if (facets && !facets.map((facet) => facet.attribute).includes(attribute)) {
          return null;
        }

        if (facets) {
          if (attribute === 'function_category') {
            return (
              <SearchFacetMenuRadios
                key={attribute}
                className="Select__inner"
                attribute={attribute}
                transformItems={transformItems}
              />
            );
          }
        }

        return (
          <SearchFacetRefinementSelect
            key={attribute}
            className="Select__inner"
            attribute={attribute}
            // @ts-expect-error
            // Title is a prop from @uncinc/uncinc-react-kitchen-sink's FormSelect component
            // used within SearchFacetRefinementSelect, which, as of yet, has no typing.
            // So we expect a Typescript error here.
            title={t(`${attribute}.label`)}
            placeholder={t(`${attribute}.placeholder`)}
            {...(attribute === 'imis_publication_year' && {
              sortBy: ['name:desc'],
            })}
          />
        );
      })}
    </SearchFacets>
  );
};

export default MetaSearchFacets;
